import React from 'react';
import CustomDropdown from "../../reusableComponents/CustomDropdown";
import '../../../css/gardenManagerStylings/reminderSection/AddReminderStylings.css';

const PRIORITY_TYPES = [
    { value: '1', label: '1 (Not a priority)' },
    { value: '2', label: '2' },
    { value: '3', label: '3 (Priority)' }
];

const RECURRENCE_TYPES = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
];

// Helper functions
const getCurrentDate = () => new Date().toISOString().split('T')[0];
const getCurrentTime = () => new Date().toTimeString().slice(0, 5);

const AddReminderComponent = ({ garden, reminderRefresh }) => {

    const [reminderTitle, setReminderTitle] = React.useState("");
    const [reminderDescription, setReminderDescription] = React.useState("");
    const [reminderPriority, setReminderPriority] = React.useState("");
    const [selectedPlantRelation, setSelectedPlantRelation] = React.useState("");
    const [reminderDate, setReminderDate] = React.useState(() => getCurrentDate());
    const [reminderTime, setReminderTime] = React.useState(() => getCurrentTime());
    const [recurrenceType, setRecurrenceType] = React.useState("none");
    const [recurrenceInterval, setRecurrenceInterval] = React.useState("");
    const [tags, setTags] = React.useState("");

    const [statusMessage, setStatusMessage] = React.useState("");
    const [statusType, setStatusType] = React.useState(""); // "success" or "error"

    const plantOptions = React.useMemo(() => {
        if (!garden?.plants) return [];

        return garden.plants.map(plant => ({
            value: plant.id,
            label: plant.plant.plant_name
        }));
    }, [garden?.plants]);

    const handleReminderChange = React.useCallback((e) => {
        setReminderTitle(e.target.value);
    }, []);

    const handleDescriptionChange = React.useCallback((e) => {
        setReminderDescription(e.target.value);
    }, []);

    const handlePriorityChange = React.useCallback((priority) => {
        setReminderPriority(priority.value);
    }, []);

    const handlePlantChange = React.useCallback((relation) => {
        setSelectedPlantRelation(relation.value);
    }, []);

    // New handlers
    const handleDateChange = React.useCallback((e) => {
        setReminderDate(e.target.value);
    }, []);

    const handleTimeChange = React.useCallback((e) => {
        setReminderTime(e.target.value);
    }, []);

    const handleRecurrenceChange = React.useCallback((recurrence) => {
        setRecurrenceType(recurrence.value);
    }, []);

    const handleRecurrenceIntervalChange = React.useCallback((e) => {
        setRecurrenceInterval(e.target.value);
    }, []);

    const handleTagsChange = React.useCallback((e) => {
        setTags(e.target.value);
    }, []);

    const handleSubmitReminder = React.useCallback(async () => {
        // Clear any existing status messages
        setStatusMessage("");
        setStatusType("");

        const reminderData = {
            title: reminderTitle,
            description: reminderDescription,
            reminder_datetime: new Date(`${reminderDate}T${reminderTime}:00`).toISOString(),
            priority: parseInt(reminderPriority),
            garden_id: garden?.id,
            plant_id: selectedPlantRelation || null,
            recurrence_type: recurrenceType !== 'none' ? { type: recurrenceType } : null,
            recurrence_interval: recurrenceInterval ? parseInt(recurrenceInterval) : null,
            tags: tags ? tags.split(',').map(tag => tag.trim()) : null,
            is_completed: false,
            is_active: true
        };

        console.log('Submitting reminder:', reminderData);

        try {
            const response = await fetch('/api/reminder/addReminder', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add your authentication header here
                },
                body: JSON.stringify(reminderData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create reminder');
            }

            const result = await response.json();

            // Reset form
            setReminderTitle("");
            setReminderDescription("");
            setReminderPriority("");
            setSelectedPlantRelation("");
            setReminderDate(getCurrentDate());
            setReminderTime(getCurrentTime());
            setRecurrenceType("none");
            setRecurrenceInterval("");
            setTags("");

            //refresh reminders to pull new ones!
            reminderRefresh();

            // Set success message
            setStatusMessage("Reminder created successfully!");
            setStatusType("success");

            // Clear the message after 5 seconds
            setTimeout(() => {
                setStatusMessage("");
                setStatusType("");
            }, 5000);

        } catch (error) {
            console.error('Error creating reminder:', error);

            // Set error message
            setStatusMessage(error.message || "Failed to create reminder. Please try again.");
            setStatusType("error");

            // Clear the message after 5 seconds
            setTimeout(() => {
                setStatusMessage("");
                setStatusType("");
            }, 5000);
        }
    }, [reminderTitle, reminderDescription, reminderPriority, selectedPlantRelation, reminderDate, reminderTime, recurrenceType, recurrenceInterval, tags, garden]);

    return (
        <div className="add-reminder-body">
            <div className={`garden-manager-add-reminders-title-input`}>
                <div className={`add-reminder-field add-reminder-value-field`}>
                    <label className="add-new-reminder-field-title">
                        Reminder Title *
                    </label>
                    <input
                        type="text"
                        value={reminderTitle}
                        onChange={handleReminderChange}
                        placeholder="Enter title..."
                        className="reminder-text-input"
                        required
                    />
                </div>
            </div>

            <div className={`garden-manager-add-reminders-description-input`}>
                <div className={`add-reminder-field add-reminder-value-field`}>
                    <label className="add-new-reminder-field-title">
                        Reminder Description
                    </label>
                    <textarea
                        value={reminderDescription}
                        onChange={handleDescriptionChange}
                        placeholder="Enter a description about the reminder..."
                        className="reminder-text-input"
                        rows={3}
                    />
                </div>
            </div>

            {/* Date and Time Fields */}
            <div className={`add-reminder-datetime-container`}>
                <div className={`add-reminder-field add-reminder-date-field`}>
                    <label className="add-new-reminder-field-title">
                        Reminder Date *
                    </label>
                    <input
                        type="date"
                        value={reminderDate}
                        onChange={handleDateChange}
                        className="reminder-date-input"
                        required
                    />
                </div>

                <div className={`add-reminder-field add-reminder-time-field`}>
                    <label className="add-new-reminder-field-title">
                        Reminder Time *
                    </label>
                    <input
                        type="time"
                        value={reminderTime}
                        onChange={handleTimeChange}
                        className="reminder-time-input"
                        required
                    />
                </div>
            </div>

            <div className={`add-reminder-field`}>
                <label className="add-new-reminder-field-title">
                    Reminder Priority *
                </label>
                <CustomDropdown
                    options={PRIORITY_TYPES}
                    value={reminderPriority}
                    onChange={handlePriorityChange}
                    placeholder="Select a reminder priority..."
                    className="garden-dropdown-custom"
                />
            </div>

            <div className={`add-reminder-field`}>
                <label className="add-new-reminder-field-title">
                    Plant Relation
                </label>
                <CustomDropdown
                    options={plantOptions}
                    value={selectedPlantRelation}
                    onChange={handlePlantChange}
                    placeholder="Select a plant relation if appropriate..."
                    className="garden-dropdown-custom"
                />
            </div>

            {/* Recurrence Section */}
            <div className={`add-reminder-field`}>
                <label className="add-new-reminder-field-title">
                    Recurrence
                </label>
                <CustomDropdown
                    options={RECURRENCE_TYPES}
                    value={recurrenceType}
                    onChange={handleRecurrenceChange}
                    placeholder="Select recurrence..."
                    className="garden-dropdown-custom"
                />
            </div>

            {recurrenceType !== 'none' && (
                <div className={`add-reminder-field add-reminder-value-field`}>
                    <label className="add-new-reminder-field-title">
                        Recurrence Interval (days)
                    </label>
                    <input
                        type="number"
                        value={recurrenceInterval}
                        onChange={handleRecurrenceIntervalChange}
                        placeholder="e.g., 7 for weekly, 1 for daily..."
                        className="reminder-text-input"
                        min="1"
                    />
                </div>
            )}

            {/* Tags Field */}
            <div className={`add-reminder-field add-reminder-value-field`}>
                <label className="add-new-reminder-field-title">
                    Tags (comma-separated)
                </label>
                <input
                    type="text"
                    value={tags}
                    onChange={handleTagsChange}
                    placeholder="e.g., watering, fertilizer, pruning..."
                    className="reminder-text-input"
                />
            </div>

            {/* Submit Button */}
            <div className={`add-reminder-submit-container`}>
                <div className="status-message-container">
                    {statusMessage && (
                        <div className={`status-message status-message-${statusType}`}>
                            {statusMessage}
                        </div>
                    )}
                </div>
                <button
                    onClick={handleSubmitReminder}
                    className="add-reminder-submit-button"
                    disabled={!reminderTitle}
                >
                    Create Reminder
                </button>
            </div>
        </div>
    )
}

export default AddReminderComponent;